/**
 * Compat shim replacing react-helmet-async.
 *
 * React 19 natively hoists <title>, <meta>, and <link> tags rendered anywhere
 * in the component tree into <head> — including during SSR (streamed into the
 * document head). That makes every page's SEO tags visible to non-JS crawlers,
 * which react-helmet-async (client-only DOM mutation) never was.
 *
 * <script type="application/ld+json"> children render in place in the body;
 * Google reads JSON-LD anywhere in the document, so that is fine.
 */
import { Children, isValidElement, type ReactNode } from "react";
import { useMatches } from "@tanstack/react-router";

export function Helmet({ children }: { children?: ReactNode }) {
  const matches = useMatches();
  // Route heads own matching tags even when the page is suspended or auth-gated.
  // Preserve JSON-LD and any tags not yet migrated, without duplicate canonicals.
  const meta = matches.flatMap((match) => match.meta ?? []).filter((entry) => entry !== undefined);
  const canonical = matches.some((match) => match.links?.some((link) => link?.rel === "canonical"));
  return <>{Children.map(children, (child) => {
    if (!isValidElement<{ name?: string; property?: string; rel?: string }>(child)) return child;
    if (child.type === "title" && meta.some((entry) => "title" in entry)) return null;
    if (child.type === "link" && child.props.rel === "canonical" && canonical) return null;
    if (child.type === "meta" && meta.some((entry) =>
      (child.props.name && "name" in entry && entry.name === child.props.name) ||
      (child.props.property && "property" in entry && entry.property === child.props.property)
    )) return null;
    return child;
  })}</>;
}

export function HelmetProvider({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}
